/**
 * Enter-to-advance keyboard policy for the wizard question flow (owner UX
 * nit r2). Extracted from `WizardQuestionFlow` so the guard chain is a pure,
 * unit-testable decision and the flow file stays within the size convention.
 *
 * Scoped by what has focus:
 *   - buttons/links keep their native Enter activation untouched;
 *   - a textarea needs Ctrl/Cmd+Enter so plain Enter stays a newline;
 *   - while the calendar popover is open, Enter belongs to IT (the same
 *     presence rule its Escape ownership uses);
 *   - key AUTOREPEAT is suppressed (default action included): with step
 *     autofocus, a held Enter would otherwise chain through steps and
 *     chain-pick autofocused choice cards via their native activation;
 *   - otherwise a discrete Enter fires the SAME gated intent as the pinned
 *     Seuraava — never more (a null intent means the step cannot advance).
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export function handleWizardAdvanceKey(
  event: ReactKeyboardEvent<HTMLElement>,
  advanceIntent: (() => void) | null,
): void {
  if (event.key !== 'Enter' || event.defaultPrevented) {
    return;
  }
  if (event.repeat) {
    event.preventDefault();
    return;
  }
  if (advanceIntent === null) {
    return;
  }
  if (document.querySelector('[data-calendar-popover]') !== null) {
    return;
  }
  const target = event.target as HTMLElement;
  if (target.closest('button, a, [role="button"]') !== null) {
    return;
  }
  if (target.tagName === 'TEXTAREA' && !(event.metaKey || event.ctrlKey)) {
    return;
  }
  event.preventDefault();
  advanceIntent();
}
